import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { RefreshCw, Plug, Cloud, FileSpreadsheet, HardDrive, Database } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DEV_MODE } from "@/lib/dev-mode";

interface ConnectionItem {
  name: string;
  type: string;
  status: "active" | "expiring" | "expired" | "disconnected";
  lastSync: string | null;
  icon: React.ReactNode;
}

const statusConfig = {
  active: { label: "Active", className: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30" },
  expiring: { label: "Expiring Soon", className: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30" },
  expired: { label: "Expired", className: "bg-destructive/15 text-destructive border-destructive/30" },
  disconnected: { label: "Disconnected", className: "bg-muted text-muted-foreground border-border" },
};

const getTokenStatus = (expiresAt: string | null): "active" | "expiring" | "expired" => {
  if (!expiresAt) return "active";
  const exp = new Date(expiresAt);
  const now = new Date();
  const hoursLeft = (exp.getTime() - now.getTime()) / (1000 * 60 * 60);
  if (hoursLeft < 0) return "expired";
  if (hoursLeft < 24) return "expiring";
  return "active";
};

export const ConnectionStatusDashboard = () => {
  const queryClient = useQueryClient();

  const { data: connections = [], isLoading } = useQuery({
    queryKey: ["connection-status"],
    queryFn: async () => {
      const items: ConnectionItem[] = [];

      if (DEV_MODE) {
        // Skip fetching in dev mode if no session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return [];
      }

      // CRM integrations
      const { data: crms } = await supabase.from("crm_integrations").select("crm_type, name, status, last_sync_at");
      crms?.forEach(crm => {
        items.push({
          name: crm.name || crm.crm_type,
          type: "CRM",
          status: crm.status === "active" ? "active" : "disconnected",
          lastSync: crm.last_sync_at,
          icon: <Database className="h-4 w-4" />,
        });
      });

      // Google tokens
      const { data: google } = await supabase.from("google_tokens").select("expires_at, updated_at");
      google?.forEach(t => {
        items.push({
          name: "Google Sheets",
          type: "Spreadsheet",
          status: getTokenStatus(t.expires_at),
          lastSync: t.updated_at,
          icon: <FileSpreadsheet className="h-4 w-4" />,
        });
      });

      // Dropbox tokens
      const { data: dropbox } = await supabase.from("dropbox_tokens").select("expires_at, updated_at");
      dropbox?.forEach(t => {
        items.push({
          name: "Dropbox",
          type: "Cloud Storage",
          status: getTokenStatus(t.expires_at),
          lastSync: t.updated_at,
          icon: <Cloud className="h-4 w-4" />,
        });
      });

      // Microsoft tokens
      const { data: microsoft } = await supabase.from("microsoft_tokens").select("expires_at, updated_at");
      microsoft?.forEach(t => {
        items.push({
          name: "OneDrive / Excel",
          type: "Cloud Storage",
          status: getTokenStatus(t.expires_at),
          lastSync: t.updated_at,
          icon: <HardDrive className="h-4 w-4" />,
        });
      });

      return items;
    },
    refetchOnWindowFocus: false,
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["connection-status"] });
  };

  const formatDate = (d: string | null) => {
    if (!d) return "Never";
    return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Plug className="h-5 w-5 text-primary" />
          Connection Status
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={handleRefresh} className="gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : connections.length === 0 ? (
          <EmptyState
            icon={Plug}
            title="No connections yet"
            description="Connect a CRM, Google Sheets, Dropbox, or OneDrive to see their status here."
          />
        ) : (
          <div className="space-y-2">
            {connections.map((conn, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="text-muted-foreground">{conn.icon}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{conn.name}</p>
                  <p className="text-xs text-muted-foreground">{conn.type} · Last sync: {formatDate(conn.lastSync)}</p>
                </div>
                <Badge variant="outline" className={statusConfig[conn.status].className}>
                  {statusConfig[conn.status].label}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
